/**
 * 公共工具函数模块
 * 包含购车进度、车主认证等卡片组件通用的数据处理函数
 */

/**
 * 解析 extension 字段（JSON 字符串）为普通对象数组
 *
 * 背景说明：
 * 购车进度、车主认证等卡片数据中，extension 字段存储的是 JSON 字符串，
 * 需要解析后才能获取具体的审核历史记录。每条记录包含状态、经销商信息等。
 *
 * @param {string|undefined|null} extensionStr - extension 字段的 JSON 字符串
 *        可能来源：接口返回的 response.extension
 *        示例值：'[{"orderStatus":"已确认","dealerName":"北京店","tel":"010-12345678","color":"#E8F5E9"}]'
 * @returns {Array} 解析后的审核历史数组，解析失败时返回空数组 []
 *
 * @example
 * // 返回格式示例
 * [
 *   { orderStatus: '已确认', dealerName: '北京店', tel: '010-12345678', color: '#E8F5E9' },
 *   { orderStatus: '待确认', dealerName: '上海店', tel: '021-87654321', color: '#FFF3E0' }
 * ]
 *
 * 使用示例：
 * const extensionList = parseExtension(response.extension)
 * extensionList.forEach(item => {
 *   console.log(item.orderStatus) // '已确认'
 * })
 */
export function parseExtension(extensionStr) {
  // 防御性检查：参数不存在、非字符串或空字符串时直接返回空数组
  // 避免后续 JSON.parse 报错
  if (!extensionStr || typeof extensionStr !== 'string') {
    return []
  }

  try {
    // 解析 JSON 字符串为 JavaScript 对象
    const parsed = JSON.parse(extensionStr)

    // 确保解析结果是数组类型
    // 如果接口返回了非数组格式的数据，兜底返回空数组
    if (!Array.isArray(parsed)) {
      console.warn('parseExtension: extension 解析后不是数组类型，已返回空数组')
      return []
    }

    // 过滤掉无效的元素，确保每个元素都是对象类型
    // 防御：过滤掉 null、undefined 以及原始类型
    return parsed.filter(item => {
      return item && typeof item === 'object'
    })
  } catch (error) {
    // JSON 解析失败（通常是因为 extension 字段不是合法的 JSON 字符串）
    console.error('解析 extension 失败:', error)
    return []
  }
}

/**
 * 根据 extensionList 生成 resultTemplates 数组
 *
 * 背景说明：
 * resultTemplates 是卡片展示组件使用的数据结构，每条记录对应一个卡片/一个滑动页面。
 * 包含：resultArray（字段列表）、colour（背景色）、remark（备注/提示）
 *
 * 设计思路：
 * - 通过 fieldConfigs 配置化字段映射，使得不同业务（购车进度、车主认证）可以复用此函数
 * - render 函数用于自定义字段的渲染方式（如加粗、链接等）
 * - colour 和 remark 从原始记录中获取，如果没有则使用空字符串
 *
 * @param {Array} extensionList - parseExtension 解析后的数组
 *        来源：const extensionList = parseExtension(response.extension)
 * @param {Array} fieldConfigs - 字段配置数组，定义如何展示每个字段
 *        每个配置对象包含：
 *        - {string} label - 字段显示的标签名，如 '订单状态'
 *        - {string} valueKey - 从 extensionList 记录中取值使用的键名，如 'orderStatus'
 *        - {Function} [render] - 可选的自定义渲染函数，返回 HTML 字符串
 *          函数内部的 this.value 指向当前字段的值
 *          函数签名：function() { return string }
 *
 * @returns {Array} resultTemplates 数组，用于卡片组件渲染
 *
 * @example
 * // fieldConfigs 配置示例
 * const fieldConfigs = [
 *   {
 *     label: '订单状态',
 *     valueKey: 'orderStatus',
 *     render: function() {
 *       // this.value 就是 record.orderStatus 的值
 *       return `<span style="font-weight: bold; color: #1A1D25;">${this.value}</span>`
 *     }
 *   },
 *   {
 *     label: '经销商电话',
 *     valueKey: 'dealerPhone',
 *     render: function() {
 *       if (!this.value) return ''
 *       return `<a href="tel:${this.value}">${this.value}</a>`
 *     }
 *   },
 *   {
 *     label: '经销商名称',  // 没有 render，使用默认渲染方式
 *     valueKey: 'dealerName'
 *   }
 * ]
 *
 * @example
 * // 返回格式示例
 * [
 *   {
 *     resultArray: [
 *       { label: '订单状态', value: '已确认', render: [Function] },
 *       { label: '经销商电话', value: '010-12345678', render: [Function] },
 *       { label: '经销商名称', value: '北京店', render: undefined }
 *     ],
 *     colour: '#E8F5E9',
 *     remark: '预计3天后交付'
 *   },
 *   {
 *     resultArray: [...],
 *     colour: '#FFF3E0',
 *     remark: ''
 *   }
 * ]
 *
 * 渲染说明：
 * 在 result-content.vue 中，字段渲染逻辑如下：
 * - 如果 item.render 存在：使用 v-html 渲染自定义 HTML
 * - 如果 item.render 不存在：直接显示 item.value 文本
 *
 * 特殊说明：
 * - colour 字段：优先取 record.colour，其次取 record.color（兼容不同接口字段名）
 * - remark 字段：用于显示额外的提示信息，如预计时间、注意事项等
 */
export function generateResultTemplates(extensionList, fieldConfigs) {
  // 防御性检查：extensionList 不是数组或为空时返回空数组
  if (!Array.isArray(extensionList) || extensionList.length === 0) {
    return []
  }

  // 防御性检查：fieldConfigs 不是数组或为空时返回空数组
  // 没有字段配置就无法生成有效的数据结构
  if (!Array.isArray(fieldConfigs) || fieldConfigs.length === 0) {
    console.warn('generateResultTemplates: fieldConfigs 为空或不是数组，已返回空数组')
    return []
  }

  // 遍历 extensionList 中的每一项，生成对应的 resultTemplates
  return extensionList.map(record => {
    // 将 fieldConfigs 映射为 resultArray
    // fieldConfigs 定义了有哪些字段需要展示，以及如何取值和渲染
    const resultArray = fieldConfigs.map(config => {
      // 从记录中获取指定键名的值
      // 使用 || '' 确保值为空时得到空字符串而非 undefined
      const value = record[config.valueKey] || ''

      // 构建字段对象
      // render 函数需要绑定 this.context 为 { value }，这样在 render 函数内部可以通过 this.value 访问当前值
      const fieldItem = {
        label: config.label,           // 字段标签，如 '订单状态'
        value: value,                 // 字段值，从 record[valueKey] 获取
      }

      // 如果配置了 render 函数，则绑定到包含 value 的上下文
      // 这样在 render 函数内部可以通过 this.value 访问字段值
      if (config.render && typeof config.render === 'function') {
        fieldItem.render = config.render.bind({ value })
      }

      return fieldItem
    })

    // 返回单个卡片的完整数据结构
    return {
      resultArray: resultArray,  // 字段数组
      colour: record?.colour || record?.color || '',  // 背景色，兼容 colour 和 color 两种字段名
      remark: record?.remark || '',  // 备注/提示信息
    }
  })
}
